// Professional PDF Generator for Van Vessem Sound & Lights
// Generates Quote (Offerte) and Invoice (Factuur) PDFs

import jsPDF from 'jspdf';

// Default company details (fallback if not loaded from settings)
const DEFAULT_COMPANY = {
  companyName: 'Van Vessem Sound & Lights',
  address: 'moerschot 8',
  postalCity: '5271SN Sint-Michielsgestel',
  iban: 'NL15RABO0369677358',
  kvk: '82338485',
  btw: 'NL003671386B67',
  email: 'info@vanvessemsoundlights.nl'
};

// Euro symbol as unicode
const EURO = '\u20AC';

// Format currency
const formatCurrency = (amount) => {
  return `${EURO} ${amount.toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

// Format date to Dutch format
const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const months = ['januari', 'februari', 'maart', 'april', 'mei', 'juni', 
                  'juli', 'augustus', 'september', 'oktober', 'november', 'december'];
  return `${date.getDate()} ${months[date.getMonth()]} ${date.getFullYear()}`;
};

// Format short date
const formatShortDate = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return `${date.getDate()}-${date.getMonth() + 1}-${date.getFullYear().toString().slice(-2)}`;
};

// Draw logo (uploaded image or placeholder text)
const drawLogo = (doc, x, y, logoData) => {
  if (logoData) {
    try {
      // Add uploaded logo image
      doc.addImage(logoData, 'AUTO', x, y, 50, 25);
    } catch (e) {
      console.error('Failed to add logo image:', e);
      // Fallback to text if image fails
      drawLogoPlaceholder(doc, x, y);
    }
  } else {
    // No logo uploaded - show placeholder text
    drawLogoPlaceholder(doc, x, y);
  }
};

// Draw placeholder when no logo
const drawLogoPlaceholder = (doc, x, y) => {
  doc.setFontSize(11);
  doc.setFont(undefined, 'bold');
  doc.setTextColor(80, 80, 80);
  doc.text('VAN VESSEM', x, y + 12);
  doc.setFontSize(9);
  doc.text('SOUND & LIGHTS', x, y + 18);
};

// Draw company header (right side)
const drawCompanyHeader = (doc, company) => {
  const c = { ...DEFAULT_COMPANY, ...company };
  const rightX = 130;
  const startY = 20;
  
  doc.setFontSize(10);
  doc.setFont(undefined, 'bold');
  doc.setTextColor(200, 100, 50); // Orange-brown color
  doc.text(c.companyName, rightX, startY);
  
  doc.setFont(undefined, 'normal');
  doc.setTextColor(100, 100, 100);
  doc.setFontSize(9);
  doc.text(c.address, rightX, startY + 5);
  doc.text(c.postalCity, rightX, startY + 10);
  
  // IBAN, KVK, BTW
  doc.setFontSize(8);
  doc.setFont(undefined, 'bold');
  doc.text('IBAN', rightX, startY + 18);
  doc.text('KVK', rightX, startY + 23);
  doc.text('BTW', rightX, startY + 28);
  
  doc.setFont(undefined, 'normal');
  doc.text(c.iban, rightX + 15, startY + 18);
  doc.text(c.kvk, rightX + 15, startY + 23);
  doc.text(c.btw, rightX + 15, startY + 28);
  
  return c; // Return merged company for use in footer
};

// Generate Quote (Offerte) PDF
export const generateQuotePDF = (data) => {
  const doc = new jsPDF();
  const { 
    quoteNumber, 
    customer, 
    customerContact,
    customerEmail,
    customerPhone,
    customerAddress,
    quoteDate, 
    productionDate,
    productionEndDate,
    location,
    items = [], 
    crew = [],
    totalExclBtw,
    btwAmount,
    totalInclBtw,
    companySignature,
    logo,
    company = {}
  } = data;
  
  // Draw logo
  drawLogo(doc, 20, 10, logo);
  
  // Draw company header and get merged company data
  const c = drawCompanyHeader(doc, company);
  
  // Customer info section
  let yPos = 55;
  doc.setFontSize(10);
  doc.setFont(undefined, 'bold');
  doc.setTextColor(200, 100, 50);
  doc.text(customer || '', 20, yPos);
  
  doc.setFont(undefined, 'normal');
  doc.setTextColor(80, 80, 80);
  doc.setFontSize(9);
  
  // Contact person
  if (customerContact && customerContact !== customer) {
    yPos += 5;
    doc.text(`t.a.v. ${customerContact}`, 20, yPos);
  }
  
  // Address
  if (customerAddress) {
    yPos += 5;
    doc.text(customerAddress, 20, yPos);
  }
  
  // Email and phone
  if (customerEmail || customerPhone) {
    yPos += 5;
    const contactLine = [customerEmail, customerPhone].filter(Boolean).join(' | ');
    doc.text(contactLine, 20, yPos);
  }
  
  // Location (if different from address)
  if (location && location !== customerAddress) {
    yPos += 5;
    doc.setFont(undefined, 'italic');
    doc.text(`Locatie: ${location}`, 20, yPos);
    doc.setFont(undefined, 'normal');
  }
  
  // Title "Offerte"
  yPos = Math.max(yPos + 15, 90);
  doc.setFontSize(24);
  doc.setFont(undefined, 'bold');
  doc.setTextColor(200, 100, 50);
  doc.text('Offerte', 20, yPos);
  
  // Quote details line
  yPos += 10;
  doc.setFontSize(9);
  doc.setFont(undefined, 'bold');
  doc.setTextColor(0, 0, 0);
  doc.text(`Offertedatum: ${formatDate(quoteDate)}`, 20, yPos);
  doc.text(`Offertenummer: ${quoteNumber}`, 75, yPos);
  
  const prodDateStr = productionEndDate 
    ? `${formatShortDate(productionDate)} t/m ${formatShortDate(productionEndDate)}`
    : formatShortDate(productionDate);
  doc.text(`Productie datum: ${prodDateStr}`, 130, yPos);
  
  // Table header
  yPos += 15;
  doc.setFontSize(9);
  doc.setFont(undefined, 'bold');
  doc.setTextColor(0, 0, 0);
  
  // Underline for header
  doc.setDrawColor(0, 0, 0);
  doc.line(20, yPos + 1, 190, yPos + 1);
  
  doc.text('Aantal', 20, yPos);
  doc.text('Omschrijving', 35, yPos);
  doc.text('Stukprijs', 130, yPos);
  doc.text('Totaal', 170, yPos);
  
  yPos += 5;
  
  // Group items by category
  const groupedItems = {};
  items.forEach(item => {
    const category = item.category || 'OVERIG';
    if (!groupedItems[category]) {
      groupedItems[category] = [];
    }
    groupedItems[category].push(item);
  });
  
  doc.setFont(undefined, 'normal');
  
  // Draw items grouped by category
  Object.entries(groupedItems).forEach(([category, categoryItems]) => {
    // Category header
    yPos += 5;
    doc.setFont(undefined, 'bold');
    doc.text(category.toUpperCase(), 35, yPos);
    doc.setFont(undefined, 'normal');
    
    categoryItems.forEach(item => {
      yPos += 5;
      doc.text(String(item.quantity || 1), 20, yPos);
      doc.text(item.name || item.description || '', 35, yPos);
      doc.text(formatCurrency(item.pricePerDay || item.price || 0), 130, yPos);
      doc.text(formatCurrency((item.pricePerDay || item.price || 0) * (item.quantity || 1)), 170, yPos);
    });
    
    // Line after category
    yPos += 3;
    doc.setDrawColor(200, 200, 200);
    doc.line(20, yPos, 190, yPos);
  });
  
  // Crew/Personnel section
  if (crew && crew.length > 0) {
    yPos += 5;
    doc.setFont(undefined, 'bold');
    doc.text('PERSONEEL', 35, yPos);
    doc.setFont(undefined, 'normal');
    
    crew.forEach(member => {
      yPos += 5;
      const days = data.days || 1;
      doc.text(String(days), 20, yPos);
      doc.text(`${member.name} - ${member.role}`, 35, yPos);
      doc.text(formatCurrency(member.ratePerDay || 0), 130, yPos);
      doc.text(formatCurrency((member.ratePerDay || 0) * days), 170, yPos);
    });
    
    yPos += 3;
    doc.setDrawColor(200, 200, 200);
    doc.line(20, yPos, 190, yPos);
  }
  
  // Totals section
  yPos += 10;
  doc.setFont(undefined, 'normal');
  
  // Subtotal (before discount)
  if (data.subtotal && data.staffelDiscount > 0) {
    doc.text('Subtotaal', 130, yPos);
    doc.text(formatCurrency(data.subtotal || 0), 170, yPos);
    
    yPos += 5;
    // Staffel discount line
    const discountText = data.staffelDetails && data.staffelDetails.length > 0 
      ? `Staffelkorting (${data.staffelDiscount}%)`
      : `Korting (${data.staffelDiscount}%)`;
    doc.setTextColor(0, 128, 0);
    doc.text(discountText, 130, yPos);
    doc.text(`- ${formatCurrency(data.discountAmount || 0)}`, 170, yPos);
    doc.setTextColor(0, 0, 0);
    
    yPos += 5;
  }
  
  doc.text('Totaal exclusief btw', 130, yPos);
  doc.text(formatCurrency(totalExclBtw || 0), 170, yPos);
  
  yPos += 5;
  doc.text('Btw-bedrag (21%)', 130, yPos);
  doc.text(formatCurrency(btwAmount || 0), 170, yPos);
  
  yPos += 5;
  doc.setFont(undefined, 'bold');
  doc.text('Totaal inclusief btw', 130, yPos);
  doc.text(formatCurrency(totalInclBtw || 0), 170, yPos);
  
  // Signature section
  yPos += 25;
  doc.setFont(undefined, 'normal');
  doc.setFontSize(9);
  doc.text(`Handtekening ${c.companyName}`, 20, yPos);
  doc.text('Handtekening Klant', 120, yPos);
  
  // Company signature placeholder
  yPos += 15;
  if (companySignature) {
    try {
      doc.addImage(companySignature, 'PNG', 20, yPos - 10, 40, 20);
    } catch (e) {
      // Ignore signature errors
    }
  }
  
  // Footer
  yPos = 270;
  doc.setFontSize(8);
  doc.setFont(undefined, 'normal');
  doc.setTextColor(100, 100, 100);
  doc.text('Gelieve deze offerte binnen 14 dagen na de offerte datum te ondertekenen.', 20, yPos);
  doc.text(`Graag mailen naar ${c.email} onder vermelding van offertenummer ${quoteNumber}`, 20, yPos + 4);
  
  return doc;
};

// Generate Invoice (Factuur) PDF
export const generateInvoicePDF = (data) => {
  const doc = new jsPDF();
  const { 
    invoiceNumber, 
    customer,
    customerContact,
    customerEmail,
    customerPhone,
    customerAddress,
    location,
    invoiceDate, 
    productionDate,
    productionEndDate,
    items = [], 
    crew = [],
    totalExclBtw,
    btwAmount,
    totalInclBtw,
    days = 1,
    logo,
    company = {}
  } = data;
  
  // Draw logo
  drawLogo(doc, 20, 10, logo);
  
  // Draw company header and get merged company data
  const c = drawCompanyHeader(doc, company);
  
  // Customer info section
  let yPos = 55;
  doc.setFontSize(10);
  doc.setFont(undefined, 'bold');
  doc.setTextColor(200, 100, 50);
  doc.text(customer || '', 20, yPos);
  
  doc.setFont(undefined, 'normal');
  doc.setTextColor(80, 80, 80);
  doc.setFontSize(9);
  
  // Contact person
  if (customerContact && customerContact !== customer) {
    yPos += 5;
    doc.text(`t.a.v. ${customerContact}`, 20, yPos);
  }
  
  // Address
  if (customerAddress) {
    yPos += 5;
    doc.text(customerAddress, 20, yPos);
  }
  
  // Email and phone
  if (customerEmail || customerPhone) {
    yPos += 5;
    const contactLine = [customerEmail, customerPhone].filter(Boolean).join(' | ');
    doc.text(contactLine, 20, yPos);
  }
  
  // Title "Factuur"
  yPos = Math.max(yPos + 15, 95);
  doc.setFontSize(24);
  doc.setFont(undefined, 'bold');
  doc.setTextColor(200, 100, 50);
  doc.text('Factuur', 20, yPos);
  
  // Invoice details line
  yPos += 10;
  doc.setFontSize(9);
  doc.setFont(undefined, 'bold');
  doc.setTextColor(0, 0, 0);
  doc.text(`Factuurdatum: ${formatDate(invoiceDate)}`, 20, yPos);
  doc.text(`Factuurnummer: ${invoiceNumber}`, 75, yPos);
  
  const prodDateStr = productionEndDate 
    ? `${formatShortDate(productionDate)} t/m ${formatShortDate(productionEndDate)}`
    : formatShortDate(productionDate);
  doc.text(`Productie datum: ${prodDateStr}`, 130, yPos);
  
  // Table header
  yPos += 15;
  doc.setFontSize(9);
  doc.setFont(undefined, 'bold');
  doc.setTextColor(0, 0, 0);
  
  // Underline for header
  doc.setDrawColor(0, 0, 0);
  doc.line(20, yPos + 1, 190, yPos + 1);
  
  doc.text('Aantal', 20, yPos);
  doc.text('Omschrijving', 35, yPos);
  doc.text('Stukprijs', 130, yPos);
  doc.text('Totaal', 170, yPos);
  
  yPos += 5;
  
  // Group items by category
  const groupedItems = {};
  items.forEach(item => {
    const category = item.category || 'OVERIG';
    if (!groupedItems[category]) {
      groupedItems[category] = [];
    }
    groupedItems[category].push(item);
  });
  
  doc.setFont(undefined, 'normal');
  
  // Draw items grouped by category
  Object.entries(groupedItems).forEach(([category, categoryItems]) => {
    // Category header
    yPos += 5;
    doc.setFont(undefined, 'bold');
    doc.text(category.toUpperCase(), 35, yPos);
    doc.setFont(undefined, 'normal');
    
    categoryItems.forEach(item => {
      yPos += 5;
      doc.text(String(item.quantity || 1), 20, yPos);
      doc.text(item.name || item.description || '', 35, yPos);
      doc.text(formatCurrency(item.pricePerDay || item.price || 0), 130, yPos);
      doc.text(formatCurrency((item.pricePerDay || item.price || 0) * (item.quantity || 1)), 170, yPos);
    });
    
    // Line after category
    yPos += 3;
    doc.setDrawColor(200, 200, 200);
    doc.line(20, yPos, 190, yPos);
  });
  
  // Crew/Personnel section
  if (crew && crew.length > 0) {
    yPos += 5;
    doc.setFont(undefined, 'bold');
    doc.text('PERSONEEL', 35, yPos);
    doc.setFont(undefined, 'normal');
    
    crew.forEach(member => {
      yPos += 5;
      doc.text(String(days), 20, yPos);
      doc.text(`${member.name} - ${member.role}`, 35, yPos);
      doc.text(formatCurrency(member.ratePerDay || 0), 130, yPos);
      doc.text(formatCurrency((member.ratePerDay || 0) * days), 170, yPos);
    });
    
    yPos += 3;
    doc.setDrawColor(200, 200, 200);
    doc.line(20, yPos, 190, yPos);
  }
  
  // Totals section
  yPos += 10;
  doc.setFont(undefined, 'normal');
  
  // Subtotal (before discount)
  if (data.subtotal && data.staffelDiscount > 0) {
    doc.text('Subtotaal', 130, yPos);
    doc.text(formatCurrency(data.subtotal || 0), 170, yPos);
    
    yPos += 5;
    // Staffel discount line
    const discountText = data.staffelDetails && data.staffelDetails.length > 0 
      ? `Staffelkorting (${data.staffelDiscount}%)`
      : `Korting (${data.staffelDiscount}%)`;
    doc.setTextColor(0, 128, 0);
    doc.text(discountText, 130, yPos);
    doc.text(`- ${formatCurrency(data.discountAmount || 0)}`, 170, yPos);
    doc.setTextColor(0, 0, 0);
    
    yPos += 5;
  }
  
  doc.text('Totaal exclusief btw', 130, yPos);
  doc.text(formatCurrency(totalExclBtw || 0), 170, yPos);
  
  yPos += 5;
  doc.text('Btw-bedrag (21%)', 130, yPos);
  doc.text(formatCurrency(btwAmount || 0), 170, yPos);
  
  yPos += 5;
  doc.setFont(undefined, 'bold');
  doc.text('Totaal inclusief btw', 130, yPos);
  doc.text(formatCurrency(totalInclBtw || 0), 170, yPos);
  
  // QR Code placeholder (right bottom)
  doc.setFontSize(8);
  doc.setFont(undefined, 'normal');
  doc.setTextColor(100, 100, 100);
  doc.text('Betaal hier', 175, 265);
  doc.setDrawColor(150, 150, 150);
  doc.rect(170, 267, 20, 20);
  doc.setFontSize(6);
  doc.text('QR', 177, 278);
  
  // Footer
  yPos = 270;
  doc.setFontSize(8);
  doc.setFont(undefined, 'normal');
  doc.setTextColor(100, 100, 100);
  doc.text('Gelieve deze factuur binnen 14 dagen na de factuurdatum te voldoen op rekeningnummer', 20, yPos);
  doc.text(`${c.iban} t.n.v. ${c.companyName} onder vermelding van factuurnummer ${invoiceNumber}`, 20, yPos + 4);
  
  return doc;
};

// Download PDF helper
export const downloadPDF = (doc, filename) => {
  doc.save(filename);
};

export default {
  generateQuotePDF,
  generateInvoicePDF,
  downloadPDF
};
